import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, CreditCard, CheckCircle, ArrowRight, Loader2 } from 'lucide-react';
import { useCart } from '../../contexts/CartContext';
import { useLanguage } from '../../contexts/LanguageContext';


// ⚙️ CONFIGURACIÓN PAYPAL — cambia este string por tu usuario PayPal.Me
// Ejemplo: si tu link es paypal.me/chimeliades → pon 'chimeliades'
const PAYPAL_ME_USER = 'CHIMELISART_PAYPAL_AQUI'; // ← CAMBIAR MAÑANA

const CheckoutOverlay: React.FC = () => {
    const { isCheckoutOpen, closeCheckout, cartItems, cartTotal } = useCart();
    const { lang } = useLanguage();
    const [email, setEmail] = useState('');
    const [step, setStep] = useState<1 | 2>(1);
    const [isProcessing, setIsProcessing] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handlePayment = async () => {
        if (!email || !email.includes('@')) {
            setError(lang === 'es' ? 'Por favor introduce un email válido' : 'Please enter a valid email');
            return;
        }

        setIsProcessing(true);
        setError(null);

        // Build a short description of the order for the PayPal note
        const description = cartItems
            .map(item => `${item.title} (${item.type})`)
            .join(' + ');

        // Round total to 2 decimal places for PayPal
        const amount = cartTotal.toFixed(2);

        // PayPal.Me URL: opens PayPal with prefilled amount in EUR
        const paypalUrl = `https://paypal.me/${PAYPAL_ME_USER}/${amount}EUR`;

        // Show step 2 confirmation, then redirect
        setStep(2);
        setTimeout(() => {
            window.open(paypalUrl, '_blank');
            // Optional: send order email to artist
            const subject = encodeURIComponent(`Pedido CHIMELISART — ${email}`);
            const body = encodeURIComponent(
                `Nueva solicitud de compra:\n\nEmail: ${email}\nTotal: ${amount} €\nObras: ${description}\n\nEl cliente ha sido redirigido a PayPal para completar el pago.`
            );
            // Silently log (don't force mailto to avoid popup blockers)
            console.log(`Order from ${email}: ${description} — ${amount}€`);
        }, 800);
    };

    if (!isCheckoutOpen) return null;

    return (
        <div className="fixed inset-0 z-[100] bg-black">
            <div className="absolute inset-0 bg-gradient-to-b from-[#1c1917] to-black opacity-90" />

            <div className="relative z-10 h-full max-w-7xl mx-auto px-4 py-8 md:py-12 flex flex-col">
                {/* Header */}
                <div className="flex justify-between items-center mb-8">
                    <h2 className="text-3xl font-heading font-bold text-[#fef3c7] tracking-widest">CHECKOUT</h2>
                    <button
                        onClick={closeCheckout}
                        className="text-white/50 hover:text-white flex items-center gap-2 transition-colors"
                    >
                        <span className="hidden md:inline text-xs uppercase tracking-widest">Back to Gallery</span>
                        <X className="w-6 h-6" />
                    </button>
                </div>

                <div className="flex-1 grid md:grid-cols-2 gap-12 overflow-hidden">
                    {/* Left: Summary */}
                    <div className="overflow-y-auto pr-4 custom-scrollbar">
                        <h3 className="text-sm uppercase text-[#fbbf24] tracking-widest mb-6 border-b border-[#fbbf24]/20 pb-2">Order Summary</h3>

                        {cartItems.length === 0 ? (
                            <div className="text-white/30 text-center py-12">Your cart is empty.</div>
                        ) : (
                            <div className="space-y-6">
                                {cartItems.map((item, idx) => (
                                    <div key={`${item.id}-${idx}`} className="flex gap-4">
                                        <div className="w-20 h-20 bg-[#292524] rounded-md overflow-hidden flex-shrink-0 flex items-center justify-center border border-white/10">
                                            {item.image ? (
                                                <img src={item.image} alt={item.title} className="w-full h-full object-cover" />
                                            ) : (
                                                <span className="text-white/20 text-[8px] uppercase font-bold text-center px-2">{item.title}</span>
                                            )}
                                        </div>
                                        <div className="flex-1">
                                            <h4 className="font-bold text-white text-lg">{item.title}</h4>
                                            <p className="text-white/50 text-xs uppercase tracking-wider mb-1">{item.type}</p>
                                            <div className="font-mono text-[#fbbf24]">{item.price === 0 ? 'Inquire' : `${item.price.toFixed(0)} €`}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        <div className="mt-8 border-t border-white/10 pt-6">
                            <div className="flex justify-between items-end">
                                <span className="text-white/50 text-sm uppercase">Total</span>
                                <span className="text-3xl font-mono text-[#fef3c7]">{cartTotal.toFixed(0)} €</span>
                            </div>
                        </div>
                    </div>

                    {/* Right: Action */}
                    <div className="bg-[#1c1917] p-8 rounded-2xl border border-white/5 h-fit shadow-2xl">
                        <AnimatePresence mode="wait">
                            {step === 1 && (
                                <motion.div
                                    key="step1"
                                    initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}
                                >
                                    <h3 className="text-xl font-bold text-white mb-6">Customer Information</h3>
                                    <div className="space-y-4 mb-6">
                                        <div className="space-y-1">
                                            <label className="text-[10px] uppercase text-white/40 tracking-widest ml-1">Email</label>
                                            <input
                                                type="email"
                                                value={email}
                                                onChange={(e) => setEmail(e.target.value)}
                                                placeholder="tu@email.com"
                                                className="w-full bg-black/50 border border-white/10 p-4 rounded-lg text-white focus:outline-none focus:border-[#fbbf24] transition-colors"
                                            />
                                        </div>
                                    </div>

                                    {error && (
                                        <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 text-red-400 text-sm rounded-lg">
                                            {error}
                                        </div>
                                    )}

                                    <button
                                        onClick={handlePayment}
                                        disabled={isProcessing || cartItems.length === 0}
                                        className="w-full bg-[#fbbf24] text-black py-4 font-bold uppercase tracking-widest hover:bg-[#f59e0b] transition-all flex justify-center items-center gap-2 disabled:opacity-50 shadow-lg shadow-[#fbbf24]/20"
                                    >
                                        {isProcessing ? (
                                            <>
                                                <Loader2 className="w-4 h-4 animate-spin" />
                                                <span>{lang === 'es' ? 'PROCESANDO...' : 'PROCESSING...'}</span>
                                            </>
                                        ) : (
                                            <>
                                                <span>{lang === 'es' ? 'CONTINUAR AL PAGO' : 'PROCEED TO PAYMENT'}</span>
                                                <ArrowRight className="w-4 h-4" />
                                            </>
                                        )}
                                    </button>

                                    <p className="mt-4 text-[10px] text-white/30 text-center uppercase tracking-widest">
                                        Pago seguro vía PayPal
                                    </p>
                                </motion.div>
                            )}

                            {step === 2 && (
                                <motion.div
                                    key="step2"
                                    initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
                                    className="text-center py-12"
                                >
                                    <div className="w-20 h-20 bg-blue-500/20 text-blue-400 rounded-full flex items-center justify-center mx-auto mb-6 text-3xl">
                                        🆀
                                    </div>
                                    <h3 className="text-2xl font-bold text-white mb-2">
                                        {lang === 'es' ? '¡Redirigiendo a PayPal!' : 'Redirecting to PayPal!'}
                                    </h3>
                                    <p className="text-white/50 mb-2 text-sm">
                                        {lang === 'es'
                                            ? 'Se ha abierto una nueva pestaña con PayPal.'
                                            : 'A new tab has opened with PayPal.'}
                                    </p>
                                    <p className="text-white/30 mb-8 text-xs">
                                        {lang === 'es'
                                            ? 'Completa el pago allí. Recibiremos tu pedido automáticamente.'
                                            : 'Complete the payment there. We will receive your order automatically.'}
                                    </p>
                                    <button
                                        onClick={closeCheckout}
                                        className="px-8 py-3 border border-[#fbbf24] text-[#fbbf24] uppercase tracking-widest hover:bg-[#fbbf24] hover:text-black transition-colors text-sm"
                                    >
                                        {lang === 'es' ? 'Volver a la Galería' : 'Back to Gallery'}
                                    </button>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CheckoutOverlay;
